import { Box, Typography } from '@mui/material';
import Image from 'next/image';
import type React from 'react';
import { CtaLink } from '@repo/segment/components/CtaLink';
import { LinkType } from '@repo/segment/enums';
import type { AnalyticsType } from '@repo/segment/types';
import { EntityPlacementCtaTypeEnum } from '@repo/supergraph/marketplace/enums';
import type { CtaType } from '@repo/supergraph/marketplace/types';
import { CPCButton } from '@repo/ui/CPCComponents/CPCButton';
import { CtaButton } from '@repo/ui/CtaButton';

type BankInstitutionCellProps = {
  image?: {
    source?: string;
    alt?: string;
  };
  cta: CtaType | null;
  displayName?: string;
  analyticsPayload: AnalyticsType;
  shouldApplyStylingUpdates?: boolean;
  shouldShowModal?: boolean;
  modalVariant?: 'cpc' | 'wealthfront';
};

export const BankInstitutionCell = ({
  image,
  cta,
  displayName,
  analyticsPayload,
  shouldApplyStylingUpdates = false,
  shouldShowModal = false,
  modalVariant = 'cpc',
}: BankInstitutionCellProps): React.JSX.Element => {
  const BankingCellContent = () => (
    <>
      {image && image.source && (
        <Image
          src={image.source}
          alt={image.alt ?? displayName ?? 'Bank Logo'}
          width={171}
          height={60}
          style={{ objectFit: 'contain' }}
        />
      )}
    </>
  );

  return (
    <Box
      sx={{
        width: { xs: 175, md: 'unset' },
        ...(shouldApplyStylingUpdates && {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }),
      }}
    >
      <Box>
        {cta ? (
          shouldShowModal ? (
            <CPCButton
              modalVariant={modalVariant}
              buttonAnalytics={{
                actionName: 'partner_logo_product_modal_cta_clicked',
                impressionSection: 'partner_logo_product_modal_cta',
              }}
              cta={cta}
              analyticsPayload={analyticsPayload}
              displayName={displayName}
              image={image}
              variant="link"
              buttonStyles={{
                padding: 0,
              }}
            >
              <BankingCellContent />
            </CPCButton>
          ) : (
            <CtaLink
              Component={Box}
              cta={{ ...cta, type: EntityPlacementCtaTypeEnum.ProductImage }}
              monetizable={analyticsPayload.monetizable}
              analyticsPayload={analyticsPayload}
              linkType={LinkType.PRODUCT_IMAGE}
              sx={{
                display: 'flex',
                '& img': {
                  maxWidth: { xs: 122, md: 171 },
                  maxHeight: { xs: 44, md: 60 },
                },
              }}
            >
              <BankingCellContent />
            </CtaLink>
          )
        ) : (
          <BankingCellContent />
        )}
      </Box>
      <Typography
        variant="textSmall"
        component="p"
        sx={{
          mt: 1,
        }}
      >
        {displayName}
      </Typography>
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          mt: 2,
          maxWidth: { xs: '189px', md: 'none' },
          ...(shouldApplyStylingUpdates && { width: '100%' }),
        }}
      >
        {cta &&
          (shouldShowModal ? (
            <CPCButton
              modalVariant={modalVariant}
              buttonAnalytics={{
                actionName: 'summary_table_product_modal_cta_clicked',
                impressionSection: 'summary_table_product_modal_cta',
              }}
              cta={cta}
              analyticsPayload={analyticsPayload}
              displayName={displayName}
              image={image}
              buttonStyles={{
                height: { xs: 29, md: 45 },
                width: '175px',
              }}
            />
          ) : (
            <CtaButton
              cta={cta}
              analyticsPayload={analyticsPayload}
              sx={{ height: { xs: 29, md: 45 } }}
              {...(shouldApplyStylingUpdates && { fullWidth: true })}
            />
          ))}
      </Box>
    </Box>
  );
};
